import { DEFAULT_PROPERTIES } from "../properties.js";
import { types } from "../../utils/types.js";
import { descriptors } from "../descriptors.js";
import { logs } from "../logs.js";
import { parsers } from "./parsers.js";
import { createSingletonLogger } from "../singleton-logger.js";

class ConfigParser {
  // Component identity for logging
  static componentName = "config-parser";
  
  #logger;
  
  constructor() {
    // Initialize the singleton logger
    this.#logger = createSingletonLogger(this);
    parsers.addType("configs", this._applyConfigs.bind(this));
  }

  _applyConfigs(configs) {
    for (const config of configs) {
      const filteredConfig = this._removeCommentedProperties(config);
      const descriptor = this._getDescriptor(filteredConfig.namespace);
      flattenConfigGroups(filteredConfig, descriptor);
      if (parsers.hasType(filteredConfig.namespace)) {
        parsers.getType(filteredConfig.namespace)(filteredConfig);
      } else {
        throw new Error("Cannot find parser for " + filteredConfig.namespace);
      }
    }
  }

  /*
    Recursively goes through the whole config and checks its properties. If property name starts with `_` or doesn't exist in its descriptor,
    removes property from config and shows warning
  */
  _removeCommentedProperties(config) {
    if (config === null || config === undefined) {
      return config;
    }

    if (typeof config === "object" && !types.isArray(config)) {
      return this._removeCommentedPropertiesFromObject(config);
    }

    if (types.isArray(config)) {
      return this._removeCommentedPropertiesFromArray(config);
    }

    return config;
  }

  _removeCommentedPropertiesFromObject(config) {
    const descriptor = this._getDescriptor(config.namespace);

    if (!config.namespace) {
      //this.#logger.error(`Config doesn't have a namespace`, config);
      //return {};
    }

    const entries = Object.entries(config);
    const filteredEntries = entries.flatMap(([key, value]) => {
      if (key.startsWith("_")) {
        return [];
      }

      const isPropertyInDescriptor = this._isPropertyInDescriptor(
        descriptor,
        config.namespace,
        key,
      );

      if (!isPropertyInDescriptor) {
        this.#logger.warn(`Property name [${key}] doesn't exist in properties of a descriptor with namespace [${config.namespace}]. Config snippet:`, JSON.stringify(config, null, 2));
        return [];
      }

      const filteredValue = this._removeCommentedProperties(value);
      return [[key, filteredValue]];
    });

    return Object.fromEntries(filteredEntries);
  }

  _removeCommentedPropertiesFromArray(config) {
    return config.map(this._removeCommentedProperties.bind(this));
  }

  _isPropertyInDescriptor(descriptor, namespace, key) {
    if (!descriptor || !namespace || DEFAULT_PROPERTIES.includes(key)) {
      return true;
    }

    const properties = descriptor.properties;

    const property = getProperty(properties, key);
    return Boolean(property);
  }

  _getDescriptor(namespace) {
    if (!namespace) {
      return null;
    }

    const descriptor = descriptors.get(namespace);
    if (descriptor) {
      return descriptor;
    }

    const lexemes = namespace.split(":");
    const descriptorNamespace = lexemes.slice(0, -1).join(":");
    const method = lexemes.at(-1);

    const shortenedNamespaceDescriptor = descriptors.get(descriptorNamespace);
    return shortenedNamespaceDescriptor?.methods[method];
  }
}

function getProperty(properties, key) {
  if (types.isArray(properties)) {
    return properties.find((property) => property.name === key);
  }

  return properties[key];
}

function flattenConfigGroups(config, descriptor) {
  if (!descriptor) return;
  const descriptorKeys = Object.keys(descriptor.properties);
  for (const key of Object.keys(config)) {
    const value = config[key];
    if (!types.isObject(value) || types.isArray(value)) continue;
    if (value.namespace || value.isCoreData) continue;
    const prefix = key + ".";
    const isGroup = descriptorKeys.some((p) => p.startsWith(prefix));
    if (!isGroup) continue;
    for (const [childKey, childValue] of Object.entries(value)) {
      config[`${key}.${childKey}`] = childValue;
    }
    delete config[key];
  }
}

const configParser = new ConfigParser();
export { ConfigParser, configParser, flattenConfigGroups };
